import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { SEASONS } from 'common/src/utils/definitions';
import { RMB_TO_USD } from '../../utils/definitions';
import { Spinner } from '@nextui-org/react';
import useDataStore from '../../stores/useDataStore';
import useUserStore from '../../stores/useUserStore';
import { getChartThemeColors } from '../../utils/chartTheme';
import SeasonWeekTooltip from './SeasonWeekTooltip';
import { getWeekFromDate } from '../../../../admin/src/utils/functions';
import { fillSeasonWeekGaps } from '../../utils/seasonWeekOrder';
import { useTranslation } from '../../hooks/useTranslation';
import { lineShadow, showIsolatedPoints } from './isolatedPoints';

interface SeasonWeekChartProps {
  series: ApexAxisChartSeries;
  xaxisCategories: string[];
  height?: number;
  yaxisTitle: string;
  priceView: 'kg' | 'box';
  selectedCurrency: string;
  tooltipCustom?: (obj: { seriesIndex: number; dataPointIndex: number; w: any }) => string;
}

const SeasonWeekChart: React.FC<SeasonWeekChartProps> = ({
  series,
  height = 350,
  yaxisTitle,
  priceView,
  selectedCurrency,
  tooltipCustom,
}) => {
  const { t } = useTranslation('market');
  const { darkMode } = useUserStore();
  const chartColors = getChartThemeColors(darkMode);
  const { selectedSeasonsMarketData, loadingMarketData } = useDataStore();


  // Memoize the color conversion functions
  const hslToHex = useMemo(() => {
    return (h: number, s: number, l: number) => {
      s /= 100;
      l /= 100;
      const k = (n: number) => (n + h / 30) % 12;
      const a = s * Math.min(l, 1 - l);
      const f = (n: number) =>
        l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
      return (
        '#' +
        [255 * f(0), 255 * f(8), 255 * f(4)]
          .map((x) => Math.round(x).toString(16).padStart(2, '0'))
          .join('')
      );
    };
  }, []);

  const lightenColor = useMemo(() => {
    return (color: string, factor: number) => {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);

      const lightenChannel = (c: number) => Math.round(c + (255 - c) * factor);

      return `#${[r, g, b]
        .map((c) => lightenChannel(c).toString(16).padStart(2, '0'))
        .join('')}`;
    };
  }, []);

  // Memoize the data transformation - IGUAL QUE SeasonComparisonWidget
  const transformedData = useMemo(() => {
    const colorCache = new Map();

    // Procesar los datos - IGUAL al SeasonComparisonWidget
    const allSeries = series.flatMap((s) => {
      // Usar el caché de colores
      const color = s.color || '#000000';
      let hexColor = colorCache.get(color);
      if (!hexColor) {
        const baseColor = color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
        hexColor = baseColor
          ? hslToHex(
            Number(baseColor[1]),
            Number(baseColor[2]),
            Number(baseColor[3])
          )
          : '#000000';
        colorCache.set(color, hexColor);
      }

      // Para cada temporada seleccionada
      return selectedSeasonsMarketData.map((seasonKey) => {
        // Obtener las fechas de inicio y fin de esta temporada
        const seasonInfo = Object.values(SEASONS).find(season => season.name === seasonKey);

        if (!seasonInfo) {
          return {
            name: `${s.name} (${seasonKey})`,
            weeklyData: [],
            hexColor,
            seasonKey,
          };
        }

        const seasonStart = seasonInfo.start.getTime();
        const seasonEnd = seasonInfo.end.getTime();

        // Calcular promedio semanal (MEDIANA) - FILTRADO POR TEMPORADA
        const weeklyData: { [key: number]: number[] } = {};

        s.data.forEach((point: any) => {
          const pointTime = new Date(point.x).getTime();

          // FILTRAR: Solo datos dentro del rango de esta temporada
          if (pointTime < seasonStart || pointTime > seasonEnd) {
            return;
          }

          // Obtener la semana del punto
          // Parse date string manually to avoid timezone issues (YYYY-MM-DD interpreted as UTC can shift day in local time)
          const [year, month, day] = String(point.x).split('-').map(Number);
          const date = new Date(year, month - 1, day);
          const weekNum = getWeekFromDate(date);

          // Skip invalid weeks
          if (isNaN(weekNum) || weekNum < 1 || weekNum > 53) {
            return;
          }

          if (!weeklyData[weekNum]) {
            weeklyData[weekNum] = [];
          }

          // Aplicar conversiones
          let value = priceView === 'box' ? point.y * 5 : point.y;
          value = selectedCurrency === 'USD' ? RMB_TO_USD(value) : value;

          // Get count from point or default to 1 (backward compatibility)
          // We use count to replicate the value n times for Weighted Median calculation
          const count = (point as any).count || 1;

          for (let i = 0; i < count; i++) {
            weeklyData[weekNum].push(value);
          }
        });

        // Convertir a array de puntos con MEDIANA
        const dataPoints = Object.entries(weeklyData).map(([week, prices]) => {
          // Calculate Median
          const sorted = prices.sort((a, b) => a - b);
          const mid = Math.floor(sorted.length / 2);
          const median = sorted.length % 2 === 0
            ? (sorted[mid - 1] + sorted[mid]) / 2
            : sorted[mid];

          return {
            week: parseInt(week),
            average: median,
          };
        });

        return {
          name: `${s.name} (${seasonKey})`,
          weeklyData: dataPoints,
          hexColor,
          seasonKey,
        };
      });
    });

    // Define distinct styles for each season - using color variations
    const seasonStyleMap: Record<string, { hueOffset: number; saturation: number; lightness: number }> = {
      '2021-2022': { hueOffset: 180, saturation: 70, lightness: 35 },  // Complementary
      '2022-2023': { hueOffset: 120, saturation: 75, lightness: 40 },  // Triadic
      '2023-2024': { hueOffset: -120, saturation: 75, lightness: 45 }, // Triadic opposite
      '2024-2025': { hueOffset: 60, saturation: 80, lightness: 50 },   // Analogous
      '2024-2025 (Estimado)': { hueOffset: 60, saturation: 80, lightness: 50 },
      '2025-2026': { hueOffset: 0, saturation: 85, lightness: 55 },    // Base color
      '2026-2027': { hueOffset: -60, saturation: 80, lightness: 45 },  // Analogous opposite
    };

    return allSeries.map((s) => {
      const baseColor = s.hexColor.match(/#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i);
      if (!baseColor) {
        return {
          name: s.name,
          weeklyData: s.weeklyData,
          color: s.hexColor,
          dashArray: 0,
        };
      }

      // Convert hex to HSL
      const r = parseInt(baseColor[1], 16) / 255;
      const g = parseInt(baseColor[2], 16) / 255;
      const b = parseInt(baseColor[3], 16) / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h = 0;

      if (max !== min) {
        const d = max - min;
        if (max === r) {
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        } else if (max === g) {
          h = ((b - r) / d + 2) / 6;
        } else {
          h = ((r - g) / d + 4) / 6;
        }
      }

      // Apply season-specific modifications
      const seasonStyle = seasonStyleMap[s.seasonKey] || { hueOffset: 0, saturation: 70, lightness: 50 };
      const newHue = ((h * 360) + seasonStyle.hueOffset + 360) % 360;
      const newColor = hslToHex(newHue, seasonStyle.saturation, seasonStyle.lightness);

      return {
        name: s.name,
        weeklyData: s.weeklyData,
        color: newColor,
      };
    });
  }, [series, hslToHex, lightenColor, priceView, selectedCurrency, selectedSeasonsMarketData]);

  // Generar categorías y series finales - IGUAL que SeasonComparisonWidget
  const { chartSeries, categories } = useMemo(() => {
    if (transformedData.length === 0) {
      return { chartSeries: [], categories: [] };
    }

    // Obtener todas las semanas disponibles de todas las series
    const allWeeks = new Set<number>();
    transformedData.forEach((s) => {
      s.weeklyData.forEach((point) => {
        allWeeks.add(point.week);
      });
    });

    if (allWeeks.size === 0) {
      return { chartSeries: [], categories: [] };
    }

    // Orden de temporada (27→53, luego 1→26), rellenando las semanas
    // intermedias sin datos para que los saltos se vean como huecos.
    const sortedWeeks = fillSeasonWeekGaps(
      Array.from(allWeeks),
      selectedSeasonsMarketData
    );

    const cats = sortedWeeks.map((weekNum) => `W${weekNum}`);

    // Mapear datos a categorías
    const chartSeries = transformedData.map((s) => {
      // Crear un mapa de semana -> promedio para búsqueda rápida
      const weekMap = new Map(
        s.weeklyData.map((point) => [point.week, point.average])
      );

      // Mapear a las semanas ordenadas
      const data = sortedWeeks.map((weekNum) => {
        const value = weekMap.get(weekNum);
        return {
          x: `W${weekNum}`,
          y: value !== undefined ? parseFloat(value.toFixed(2)) : null,
        };
      });

      return {
        name: s.name,
        data,
        color: s.color,
        type: 'line' as const,
      };
    });

    return { chartSeries: showIsolatedPoints(chartSeries), categories: cats };
  }, [transformedData, selectedSeasonsMarketData]);

  // Memoize the chart options
  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        height: height,
        type: 'line',
        toolbar: { show: false },
        foreColor: chartColors.foreColor,
        dropShadow: {
          ...lineShadow(chartSeries),
          color: '#000',
          top: 18,
          left: 7,
          blur: 10,
          opacity: 0.2,
        },
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: 3,
        curve: 'smooth',
      },
      grid: {
        borderColor: chartColors.gridBorderColor,
        row: {
          colors: chartColors.gridRowColors,
          opacity: 0.5,
        },
        xaxis: {
          lines: {
            show: true,
          },
        },
      },
      xaxis: {
        type: 'category',
        categories: categories,
        title: {
          text: 'Semana del Año',
        },
        axisBorder: {
          show: true,
        },
        axisTicks: {
          show: true,
        },
        labels: {
          rotate: -45,
          rotateAlways: false,
        },
      },
      yaxis: {
        title: {
          text: yaxisTitle,
        },
        labels: {
          formatter: (value) =>
            `${Math.round(value)} ${selectedCurrency === 'RMB' ? '¥' : '$'}`,
        },
      },
      tooltip: {
        shared: true,
        custom: ({ seriesIndex, dataPointIndex, w }: any) => {
          if (tooltipCustom) {
            return tooltipCustom({ seriesIndex, dataPointIndex, w });
          }
          return SeasonWeekTooltip({
            seriesIndex,
            dataPointIndex,
            w,
            selectedCurrency,
            priceView,
          });
        },
      },
      markers: {
        size: 5,
      },
      legend: {
        position: 'bottom',
      },
    }),
    [height, yaxisTitle, selectedCurrency, priceView, categories, chartSeries]
  );



  if (loadingMarketData) {
    return (
      <div className='w-full h-[350px] flex items-center justify-center bg-default-50 rounded-lg'>
        <div className='flex flex-col items-center gap-2'>
          <Spinner />
          <span className='text-sm text-default-500'>
            {t('Procesando datos históricos...')}
          </span>
          <p className='text-sm text-default-500'>
            {t('Este proceso puede tardar unos minutos. Estamos recopiando todos los datos de las temporadas seleccionadas.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <ReactApexChart
      options={options}
      series={chartSeries}
      type='line'
      height={height}
    />
  );
};

export default React.memo(SeasonWeekChart);
